import { parseAsStringEnum, useQueryState } from 'nuqs';

import '@/shared/features/quiz-folder/consts/events.quiz-folder';

import { trackEvent } from '@zep/logger/tracking';

import { QuizsetCursorSearchReqContentType } from '@/api/gen/_model/quizsetCursorSearchReqContentType';

export type LibraryContentType = QuizsetCursorSearchReqContentType;

/** 사용자가 체크하는 축. 서버 `contentType` 은 이 둘을 접은 결과다. */
export type LibraryContentKind = 'QUIZSET' | 'FOLDER';

export const CONTENT_TYPE_SECTION_LABEL_KEY = 'common:public.contentType.label';

export const CONTENT_TYPE_OPTIONS = [
  { value: 'QUIZSET', labelKey: 'common:public.contentType.quiz' },
  { value: 'FOLDER', labelKey: 'common:public.contentType.folder' },
] as const satisfies readonly {
  value: LibraryContentKind;
  labelKey: string;
}[];

const isAllContentType = (
  contentType: LibraryContentType,
): contentType is typeof QuizsetCursorSearchReqContentType.ALL =>
  contentType === QuizsetCursorSearchReqContentType.ALL;

/** 체크 상태 판정. `ALL`(기본값)은 둘 다 켜진 것으로 본다. */
export const isContentTypeSelected = (
  contentType: LibraryContentType,
  kind: LibraryContentKind,
) => isAllContentType(contentType) || contentType === kind;

/**
 * 마지막으로 남은 체크인가. 둘 다 끈 상태는 없으므로 이 항목은 끌 수 없다 — 선택된 채로 잠근다.
 */
export const isContentTypeLocked = (
  contentType: LibraryContentType,
  kind: LibraryContentKind,
) => !isAllContentType(contentType) && contentType === kind;

/** 체크를 토글한 뒤의 서버 값. 잠긴 항목(`isContentTypeLocked`)은 호출하지 않는다. */
const nextContentType = (
  contentType: LibraryContentType,
  kind: LibraryContentKind,
): LibraryContentType => {
  if (isAllContentType(contentType)) {
    return kind === 'QUIZSET' ? 'FOLDER' : 'QUIZSET';
  }
  return QuizsetCursorSearchReqContentType.ALL;
};

/**
 * 자료실의 자료 유형 필터 (퀴즈 · 폴더 체크박스).
 *
 * 값은 URL 쿼리에 싣는다 — 목록 쿼리 키에 들어가 필터를 바꾸면 커서가 버려지고 새 목록을 받는다.
 */
export const useLibraryContentTypeQuery = () => {
  const [contentType, setContentType] = useQueryState(
    'type',
    parseAsStringEnum<LibraryContentType>(
      Object.values(QuizsetCursorSearchReqContentType),
    ).withDefault(QuizsetCursorSearchReqContentType.ALL),
  );

  const write = (next: LibraryContentType) =>
    void setContentType(next, { history: 'replace', shallow: true });

  return {
    contentType,
    // 사용자가 체크박스를 건드린 것만 "유형 변경"이다. 화면이 되돌리는 `reset` 은 찍지 않는다.
    toggleContentType: (kind: LibraryContentKind) => {
      // 잠긴 항목은 값이 바뀌지 않으므로 변경 이벤트도 찍지 않는다.
      if (isContentTypeLocked(contentType, kind)) return;
      const next = nextContentType(contentType, kind);
      trackEvent('quiz_library_content_type_change', {
        before_library_content_type: contentType,
        after_library_content_type: next,
      });
      write(next);
    },
    resetContentType: () => write(QuizsetCursorSearchReqContentType.ALL),
  };
};
